// Live agent fleet: the fixed roster (config/roster.js — one house per agent)
// merged with Firestore `agents` docs, which Grok Bot writes through the CRM MCP
// server (report_agent_status). Agents with no doc yet show as offline.
//
// Returned agents have the shape:
// { id, name, world, district, autonomous, status, task, progress, upNext[],
//   message, link, doneToday, level, updatedAt, reporting }

import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { makeAgentsApi } from "../lib/agentsApi";
import { ROSTER } from "../config/roster";
import { NEEDS_YOU, STATUS } from "../config/worlds";

// An agent that claims "working" but hasn't reported in this long is shown as
// blocked. Self-reported status is narration; silence is evidence. Grok Bot
// runs on schedules, not a tight loop, so this is generous.
const STALE_MS = 30 * 60 * 1000;

const todayEastern = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

export function useAgents({ db, user }) {
  const [docs, setDocs] = useState({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!db) return undefined;
    return onSnapshot(
      collection(db, "agents"),
      (snap) => {
        const next = {};
        snap.docs.forEach((d) => (next[d.id] = d.data()));
        setDocs(next);
        setReady(true);
        setError(null);
      },
      (err) => {
        console.error("[agents] snapshot failed:", err);
        setError(err.message);
        setReady(true);
      }
    );
  }, [db]);

  // re-evaluate staleness + "done today" rollover without new snapshots
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const agents = useMemo(() => {
    const now = Date.now();
    const today = todayEastern();
    const known = new Set(ROSTER.map((a) => a.id));
    const rows = ROSTER.map((r) => [r, docs[r.id]]);
    // agents Grok reported that aren't on the roster yet still show in the
    // ledger (they just have no house until config/layout.js gets a spot)
    Object.keys(docs)
      .filter((id) => !known.has(id))
      .forEach((id) => rows.push([{ id, name: docs[id].name || id, world: docs[id].world || "internal", district: docs[id].district || "Unassigned", autonomous: true }, docs[id]]));

    return rows.map(([r, v]) => {
      if (!v) {
        return { ...r, status: STATUS.OFFLINE, task: "", progress: 0, upNext: [], message: "", link: "", doneToday: 0, level: 1, updatedAt: null, reporting: false };
      }
      const a = {
        ...r,
        status: v.status ?? STATUS.IDLE,
        task: v.task ?? "",
        progress: Number(v.progress ?? 0),
        upNext: Array.isArray(v.upNext) ? v.upNext : [],
        message: v.message ?? "",
        link: v.link ?? "",
        doneToday: v.doneTodayDate === today ? Number(v.doneToday ?? 0) : 0,
        level: Number(v.level ?? 1),
        updatedAt: v.updatedAt ?? null,
        reporting: true,
      };
      const ts = a.updatedAt?.toMillis?.() ?? 0;
      if (a.status === STATUS.WORKING && ts && now - ts > STALE_MS) {
        const mins = Math.round((now - ts) / 60000);
        return { ...a, status: STATUS.BLOCKED, message: `No update for ${mins >= 120 ? `${Math.round(mins / 60)} hr` : `${mins} min`} — last said it was working.` };
      }
      return a;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, tick]);

  const api = useMemo(() => makeAgentsApi(db, user), [db, user]);

  const derived = useMemo(() => {
    const byWorld = { leads: [], clients: [], internal: [] };
    agents.forEach((a) => (byWorld[a.world] ?? byWorld.internal).push(a));
    const needsYou = agents.filter((a) => NEEDS_YOU.includes(a.status));
    const working = agents.filter((a) => a.status === STATUS.WORKING).length;
    const reporting = agents.filter((a) => a.reporting).length;
    const doneToday = agents.reduce((s, a) => s + a.doneToday, 0);
    return { byWorld, needsYou, working, reporting, doneToday };
  }, [agents]);

  return { agents, ...derived, api, ready, error };
}
